/**
 * 通知管理器
 * 提供统一的浏览器通知管理功能，包括桌面通知、标题闪烁、声音提醒等
 */

import { MessageInfo } from '@/api/types/chat'

export interface NotificationSettings {
  // 桌面通知设置
  desktopEnabled: boolean
  desktopSound: boolean
  desktopPreview: boolean  // 是否显示消息预览
  
  // 标签页通知设置
  titleFlash: boolean
  titleShowCount: boolean  // 是否在标题显示未读数量
  
  // 声音设置
  soundEnabled: boolean
  soundVolume: number  // 0-1
  
  // 消息过滤
  muteGroups: string[]  // 静音的群组ID列表
  muteFriends: string[] // 静音的好友ID列表
}

class NotificationManager {
  private static instance: NotificationManager
  private settings: NotificationSettings
  private originalTitle: string = ''
  private titleFlashInterval: number | null = null
  private unreadCount = 0
  private audioContext: AudioContext | null = null
  private notificationSound: AudioBuffer | null = null
  private isDocumentVisible = true

  // 默认设置
  private defaultSettings: NotificationSettings = {
    desktopEnabled: true,
    desktopSound: true,
    desktopPreview: true,
    titleFlash: true,
    titleShowCount: true,
    soundEnabled: true,
    soundVolume: 0.5,
    muteGroups: [],
    muteFriends: []
  }

  private constructor() {
    this.settings = this.loadSettings()
    this.originalTitle = document.title
    this.initializeAudio()
    this.setupVisibilityListener()
  }

  public static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager()
    }
    return NotificationManager.instance
  }

  /**
   * 初始化音频上下文
   */
  private async initializeAudio() {
    try {
      // 创建音频上下文
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      
      // 加载通知音效
      await this.loadNotificationSound()
    } catch (error) {
      console.warn('音频初始化失败:', error)
    }
  }

  /**
   * 加载通知音效
   */
  private async loadNotificationSound() {
    try {
      if (!this.audioContext) return

      // 使用 Web Audio API 生成简单的通知音效
      const duration = 0.3
      const sampleRate = this.audioContext.sampleRate
      const buffer = this.audioContext.createBuffer(1, duration * sampleRate, sampleRate)
      const data = buffer.getChannelData(0)

      // 生成简单的铃声（两个音调）
      for (let i = 0; i < buffer.length; i++) {
        const t = i / sampleRate
        const freq1 = 800 // 第一个音调频率
        const freq2 = 1000 // 第二个音调频率
        
        if (t < duration / 2) {
          data[i] = Math.sin(2 * Math.PI * freq1 * t) * 0.3 * Math.exp(-t * 3)
        } else {
          data[i] = Math.sin(2 * Math.PI * freq2 * t) * 0.3 * Math.exp(-t * 3)
        }
      }

      this.notificationSound = buffer
    } catch (error) {
      console.warn('加载通知音效失败:', error)
    }
  }

  /**
   * 设置文档可见性监听器
   */
  private setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      this.isDocumentVisible = !document.hidden
      
      if (this.isDocumentVisible) {
        // 当页面重新可见时，停止标题闪烁并重置标题
        this.stopTitleFlash()
        this.updateTitle()
      }
    })
  }

  /**
   * 请求通知权限
   */
  public async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('浏览器不支持通知功能')
      return 'denied'
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      return permission
    }

    return Notification.permission
  }


  /**
   * 检查消息是否被静音
   */
  private isMessageMuted(message: MessageInfo): boolean {
    if (message.session_type === 'group') {
      return this.settings.muteGroups.includes(message.conversation_id)
    } else {
      return this.settings.muteFriends.includes(message.sender_id)
    }
  }

  /**
   * 显示桌面通知
   */
  private async showDesktopNotification(message: MessageInfo) {
    if (!this.settings.desktopEnabled) return
    if (!('Notification' in window) || Notification.permission !== 'granted') return

    const senderName = message.senderName || message.sender_id
    let content = '新消息'
    let icon = message.senderAvatar || '/icon.png'

    // 根据设置决定是否显示消息预览
    if (this.settings.desktopPreview && message.decryptedContentData?.content) {
      content = message.decryptedContentData.content
      
      // 对长消息进行截断
      if (content.length > 50) {
        content = content.substring(0, 50) + '...'
      }
    }

    // 为群组消息添加群组标识
    const title = message.session_type === 'group' 
      ? `${senderName} (群组)` 
      : senderName

    try {
      const notification = new Notification(title, {
        body: content,
        icon: icon,
        badge: '/icon.png',
        tag: message.conversation_id, // 同一对话的通知会互相替换
        requireInteraction: false,
        silent: !this.settings.desktopSound
      })

      // 设置通知点击处理
      notification.onclick = () => {
        this.handleNotificationClick(message)
        notification.close()
      }

      // 设置自动关闭
      setTimeout(() => {
        notification.close()
      }, 5000)

    } catch (error) {
      console.error('显示桌面通知失败:', error)
    }
  }

  /**
   * 处理通知点击事件
   */
  private handleNotificationClick(message: MessageInfo) {
    try {
      // 聚焦窗口
      if (window.focus) {
        window.focus()
      }

      // 触发自定义事件，让主应用处理导航
      const event = new CustomEvent('notification-click', {
        detail: {
          message,
          conversationId: message.conversation_id,
          senderId: message.sender_id,
          sessionType: message.session_type
        }
      })
      window.dispatchEvent(event)

    } catch (error) {
      console.error('处理通知点击失败:', error)
    }
  }

  /**
   * 播放通知音效
   */
  private playNotificationSound() {
    if (!this.settings.soundEnabled) return
    if (!this.audioContext || !this.notificationSound) return

    try {
      // 检查音频上下文状态
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume()
      }

      const source = this.audioContext.createBufferSource()
      const gainNode = this.audioContext.createGain()
      
      source.buffer = this.notificationSound
      gainNode.gain.value = this.settings.soundVolume
      
      source.connect(gainNode)
      gainNode.connect(this.audioContext.destination)
      
      source.start()
    } catch (error) {
      console.warn('播放通知音效失败:', error)
    }
  }

  /**
   * 开始标题闪烁
   */
  private startTitleFlash() {
    if (!this.settings.titleFlash || this.isDocumentVisible) return
    if (this.titleFlashInterval) return // 已经在闪烁

    let isFlashing = false
    this.titleFlashInterval = window.setInterval(() => {
      if (isFlashing) {
        document.title = this.getTitle()
      } else {
        document.title = '💬 新消息 - ' + this.originalTitle
      }
      isFlashing = !isFlashing
    }, 1000)
  }

  /**
   * 停止标题闪烁
   */
  private stopTitleFlash() {
    if (this.titleFlashInterval) {
      clearInterval(this.titleFlashInterval)
      this.titleFlashInterval = null
    }
  }

  /**
   * 获取标题文本
   */
  private getTitle(): string {
    if (this.settings.titleShowCount && this.unreadCount > 0) {
      return `(${this.unreadCount}) ${this.originalTitle}`
    }
    return this.originalTitle
  }

  /**
   * 更新标题
   */
  private updateTitle() {
    if (!this.titleFlashInterval) {
      document.title = this.getTitle()
    }
  }

  /**
   * 显示消息通知（主方法）
   */
  public async showNotification(message: MessageInfo) {
    // 检查消息是否被静音
    if (this.isMessageMuted(message)) return

    // 增加未读计数
    this.unreadCount++

    // 更新标题
    this.updateTitle()

    // 如果页面不可见，开始标题闪烁
    if (!this.isDocumentVisible) {
      this.startTitleFlash()
    }

    // 显示桌面通知
    await this.showDesktopNotification(message)

    // 播放通知音效
    this.playNotificationSound()
  }

  /**
   * 清除未读计数
   */
  public clearUnreadCount() {
    this.unreadCount = 0
    this.stopTitleFlash()
    this.updateTitle()
  }

  /**
   * 设置未读计数
   */
  public setUnreadCount(count: number) {
    this.unreadCount = Math.max(0, count)
    this.updateTitle()
    
    if (count === 0) {
      this.stopTitleFlash()
    }
  }

  /**
   * 更新设置
   */
  public updateSettings(newSettings: Partial<NotificationSettings>) {
    this.settings = { ...this.settings, ...newSettings }
    this.saveSettings()
  }

  /**
   * 获取当前设置
   */
  public getSettings(): NotificationSettings {
    return { ...this.settings }
  }

  /**
   * 静音/取消静音对话
   */
  public toggleMute(conversationId: string, sessionType: 'group' | 'direct') {
    if (sessionType === 'group') {
      const index = this.settings.muteGroups.indexOf(conversationId)
      if (index > -1) {
        this.settings.muteGroups.splice(index, 1)
      } else {
        this.settings.muteGroups.push(conversationId)
      }
    } else {
      const index = this.settings.muteFriends.indexOf(conversationId)
      if (index > -1) {
        this.settings.muteFriends.splice(index, 1)
      } else {
        this.settings.muteFriends.push(conversationId)
      }
    }
    this.saveSettings()
  }

  /**
   * 检查对话是否被静音
   */
  public isMuted(conversationId: string, sessionType: 'group' | 'direct'): boolean {
    if (sessionType === 'group') {
      return this.settings.muteGroups.includes(conversationId)
    } else {
      return this.settings.muteFriends.includes(conversationId)
    }
  }

  /**
   * 加载设置
   */
  private loadSettings(): NotificationSettings {
    try {
      const saved = localStorage.getItem('notification_settings')
      if (saved) {
        return { ...this.defaultSettings, ...JSON.parse(saved) }
      }
    } catch (error) {
      console.warn('加载通知设置失败:', error)
    }
    return { ...this.defaultSettings }
  }

  /**
   * 保存设置
   */
  private saveSettings() {
    try {
      localStorage.setItem('notification_settings', JSON.stringify(this.settings))
    } catch (error) {
      console.warn('保存通知设置失败:', error)
    }
  }

  /**
   * 重置设置为默认值
   */
  public resetSettings() {
    this.settings = { ...this.defaultSettings }
    this.saveSettings()
  }

  /**
   * 销毁管理器
   */
  public destroy() {
    this.stopTitleFlash()
    if (this.audioContext) {
      this.audioContext.close()
    }
  }
}

export default NotificationManager
